import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Grid,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  GroupAdd as GroupAddIcon,
  RemoveCircleOutline as RemoveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getTrail, deleteTrail, listCloudTrailAccounts, addTrailAccounts, removeTrailAccounts } from '../../services/cloudtrailApi';

const statusColors = {
  completed: { bg: '#E8F5E9', color: '#2E7D32' },
  in_progress: { bg: '#E3F2FD', color: '#1565C0' },
  partial_failure: { bg: '#FFF3E0', color: '#E65100' },
  failed: { bg: '#FFEBEE', color: '#C62828' },
  pending: { bg: '#F5F5F5', color: '#616161' },
  escalating: { bg: '#E3F2FD', color: '#1565C0' },
  creating_bucket: { bg: '#E3F2FD', color: '#1565C0' },
  creating_trail: { bg: '#E3F2FD', color: '#1565C0' },
  cleaning_up: { bg: '#E3F2FD', color: '#1565C0' },
  deleting: { bg: '#E3F2FD', color: '#1565C0' },
  deleting_trail: { bg: '#E3F2FD', color: '#1565C0' },
  deleting_bucket: { bg: '#E3F2FD', color: '#1565C0' },
  updating: { bg: '#E3F2FD', color: '#1565C0' },
  updating_trail: { bg: '#E3F2FD', color: '#1565C0' },
  adding_trail: { bg: '#E3F2FD', color: '#1565C0' },
  removing_trail: { bg: '#E3F2FD', color: '#1565C0' },
  deleted: { bg: '#F5F5F5', color: '#616161' },
};

const statusLabels = {
  completed: 'Completed',
  in_progress: 'In Progress',
  partial_failure: 'Partial Failure',
  failed: 'Failed',
  pending: 'Pending',
  escalating: 'Escalating',
  creating_bucket: 'Creating Bucket',
  creating_trail: 'Creating Trail',
  cleaning_up: 'Cleaning Up',
  deleting: 'Deleting',
  deleting_trail: 'Deleting Trail',
  deleting_bucket: 'Deleting Bucket',
  updating: 'Updating',
  updating_trail: 'Updating Trail',
  adding_trail: 'Adding Trail',
  removing_trail: 'Removing Trail',
  deleted: 'Deleted',
};

const TrailDetail = () => {
  const { trailId } = useParams();
  const navigate = useNavigate();
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBucket, setDeleteBucket] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addAccountsDialogOpen, setAddAccountsDialogOpen] = useState(false);
  const [removeAccountDialogOpen, setRemoveAccountDialogOpen] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState(null);
  const [realmAccounts, setRealmAccounts] = useState([]);
  const [selectedNewAccounts, setSelectedNewAccounts] = useState([]);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [addingAccounts, setAddingAccounts] = useState(false);
  const [removingAccount, setRemovingAccount] = useState(false);
  const pollRef = useRef(null);

  const fetchTrail = useCallback(async () => {
    try {
      const response = await getTrail(trailId);
      setTrail(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [trailId]);

  useEffect(() => {
    fetchTrail();
  }, [fetchTrail]);

  // Poll while any account is still in progress
  useEffect(() => {
    if (!trail) return;

    const activeStatuses = ['pending', 'escalating', 'creating_bucket', 'creating_trail', 'cleaning_up', 'deleting_trail', 'deleting_bucket', 'updating_trail', 'adding_trail', 'removing_trail'];
    const hasInProgress = trail.accountStatuses?.some((s) =>
      activeStatuses.includes(s.status)
    );

    if (hasInProgress) {
      pollRef.current = setInterval(async () => {
        const data = await fetchTrail();
        if (!data) {
          // Trail was hard-deleted from DB — navigate to list
          clearInterval(pollRef.current);
          pollRef.current = null;
          navigate('/');
          return;
        }
        const stillInProgress = data.accountStatuses?.some((s) =>
          activeStatuses.includes(s.status)
        );
        if (!stillInProgress && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [trail, fetchTrail]);

  const toggleError = (accountId) => {
    setExpandedErrors((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteTrail(trailId, deleteBucket);
      setDeleteDialogOpen(false);
      setDeleteBucket(false);
      // Refresh to show "deleting" status — polling will pick up the rest
      const data = await fetchTrail();
      if (!data) {
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const isTrailBusy = trail && (trail.status === 'in_progress' || trail.status === 'deleting' || trail.status === 'updating');

  const handleOpenAddAccounts = async () => {
    try {
      const response = await listCloudTrailAccounts();
      const allAccounts = response.data?.accounts || [];
      const currentIds = trail.targetAccountIds || [];
      const available = allAccounts.filter((a) => !currentIds.includes(a.accountId));
      setRealmAccounts(available);
      setSelectedNewAccounts([]);
      setAccountSearchQuery('');
      setAddAccountsDialogOpen(true);
    } catch (err) {
      toast.error(`Failed to load accounts: ${err.message}`);
    }
  };

  const handleAddAccountsConfirm = async () => {
    try {
      setAddingAccounts(true);
      await addTrailAccounts(trailId, selectedNewAccounts);
      setAddAccountsDialogOpen(false);
      setSelectedNewAccounts([]);
      setAccountSearchQuery('');
      toast.success('Account addition started');
      fetchTrail();
    } catch (err) {
      toast.error(`Failed to add accounts: ${err.message}`);
    } finally {
      setAddingAccounts(false);
    }
  };

  const handleOpenRemoveAccount = (acct) => {
    setAccountToRemove(acct);
    setRemoveAccountDialogOpen(true);
  };

  const handleRemoveAccountConfirm = async () => {
    try {
      setRemovingAccount(true);
      await removeTrailAccounts(trailId, [accountToRemove.accountId]);
      setRemoveAccountDialogOpen(false);
      setAccountToRemove(null);
      toast.success('Account removal started');
      fetchTrail();
    } catch (err) {
      toast.error(`Failed to remove account: ${err.message}`);
    } finally {
      setRemovingAccount(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  if (error || !trail) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2, textTransform: 'none' }}>
          Back to Trails
        </Button>
        <Typography color="error">{error || 'Trail not found'}</Typography>
      </Box>
    );
  }

  const sc = statusColors[trail.status] || statusColors.pending;

  const renderEventSummary = () => {
    const ec = trail.eventConfig;
    if (!ec) return 'None configured';

    const parts = [];
    if (ec.managementEvents?.enabled) parts.push('Management');
    if (ec.dataEvents?.length > 0) parts.push(`${ec.dataEvents.length} Data`);
    if (ec.insightSelectors?.length > 0) parts.push('Insights');
    if (ec.networkActivityEvents?.length > 0) parts.push(`${ec.networkActivityEvents.length} Network`);
    return parts.length > 0 ? parts.join(', ') : 'None configured';
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          textTransform: 'none',
          color: '#666',
          fontFamily: 'Metropolis, sans-serif',
          mb: 2,
        }}
      >
        Back to Trails
      </Button>

      {/* Trail Metadata */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #E5E5E5', borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            {trail.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={statusLabels[trail.status] || trail.status}
              sx={{
                backgroundColor: sc.bg,
                color: sc.color,
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<GroupAddIcon />}
              onClick={handleOpenAddAccounts}
              disabled={isTrailBusy}
              sx={{
                borderColor: '#1565C0',
                color: '#1565C0',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': { borderColor: '#0D47A1', backgroundColor: 'rgba(21, 101, 192, 0.04)' },
                '&.Mui-disabled': { borderColor: '#E0E0E0', color: '#BDBDBD' },
              }}
            >
              Add Accounts
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/trails/${trailId}/edit`)}
              disabled={isTrailBusy}
              sx={{
                borderColor: '#1565C0',
                color: '#1565C0',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': { borderColor: '#0D47A1', backgroundColor: 'rgba(21, 101, 192, 0.04)' },
                '&.Mui-disabled': { borderColor: '#E0E0E0', color: '#BDBDBD' },
              }}
            >
              Edit Trail
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloneIcon />}
              onClick={() => navigate(`/trails/create?cloneFrom=${trailId}`)}
              disabled={isTrailBusy}
              sx={{
                borderColor: '#1565C0',
                color: '#1565C0',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': { borderColor: '#0D47A1', backgroundColor: 'rgba(21, 101, 192, 0.04)' },
                '&.Mui-disabled': { borderColor: '#E0E0E0', color: '#BDBDBD' },
              }}
            >
              Clone Trail
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isTrailBusy}
              sx={{
                borderColor: '#C62828',
                color: '#C62828',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': { borderColor: '#B71C1C', backgroundColor: 'rgba(198, 40, 40, 0.04)' },
                '&.Mui-disabled': { borderColor: '#E0E0E0', color: '#BDBDBD' },
              }}
            >
              Delete Trail
            </Button>
          </Box>
        </Box>

        {trail.errorMessage && (
          <Typography variant="body2" sx={{ color: '#C62828', mb: 2 }}>
            {trail.errorMessage}
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              S3 Bucket
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {trail.s3BucketName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              Multi-Region
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {trail.isMultiRegion ? 'Yes' : 'No'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              Region
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {trail.region || 'us-east-1'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              Events
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {renderEventSummary()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              Created By
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {trail.createdByEmail}
            </Typography>
          </Grid>
          {trail.s3Prefix && (
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
                S3 Prefix
              </Typography>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                {trail.s3Prefix}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
              Created At
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
              {new Date(trail.createdAt).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      </motion.div>

      {/* Per-Account Status */}
      <motion.div variants={sectionVariants}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'Metropolis, sans-serif',
          fontWeight: 600,
          color: '#253E66',
          mb: 2,
        }}
      >
        Account Status ({trail.accountStatuses?.length || 0} accounts)
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
              {['Account', 'Status', 'Phase', 'Trail ARN', 'Error', 'Updated', 'Actions'].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                    fontSize: '0.8rem',
                    borderBottom: '2px solid #E5E5E5',
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(trail.accountStatuses || []).map((acct, index) => {
              const asc = statusColors[acct.status] || statusColors.pending;
              return (
                <React.Fragment key={acct.accountId}>
                  <TableRow sx={{
                    '@keyframes fadeInRow': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'none' },
                    },
                    animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                  }}>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.875rem' }}>
                        {acct.accountName}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999', fontSize: '0.75rem' }}>
                        {acct.accountId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[acct.status] || acct.status}
                        size="small"
                        sx={{
                          backgroundColor: asc.bg,
                          color: asc.color,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', fontSize: '0.875rem' }}>
                      {acct.phase}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', fontSize: '0.75rem', maxWidth: 200, wordBreak: 'break-all' }}>
                      {acct.trailArn || '-'}
                    </TableCell>
                    <TableCell>
                      {acct.errorMessage ? (
                        <Box>
                          <IconButton size="small" onClick={() => toggleError(acct.accountId)}>
                            {expandedErrors[acct.accountId] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                          <Collapse in={expandedErrors[acct.accountId]}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#C62828', fontSize: '0.75rem', mt: 0.5, maxWidth: 300, wordBreak: 'break-word' }}
                            >
                              {acct.errorMessage}
                            </Typography>
                          </Collapse>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', fontSize: '0.8rem' }}>
                      {new Date(acct.updatedAt).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRemoveAccount(acct)}
                        disabled={
                          isTrailBusy ||
                          acct.accountId === trail.s3BucketAccountId ||
                          (trail.accountStatuses?.length || 0) <= 1
                        }
                        sx={{
                          color: '#C62828',
                          '&.Mui-disabled': { color: '#E0E0E0' },
                        }}
                        title={
                          acct.accountId === trail.s3BucketAccountId
                            ? 'Cannot remove the S3 bucket account'
                            : (trail.accountStatuses?.length || 0) <= 1
                              ? 'Cannot remove the last account'
                              : 'Remove account'
                        }
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeleteBucket(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
          Delete Trail
        </DialogTitle>
        <DialogContent>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', mb: 2 }}>
            This will delete the trail <strong>{trail.name}</strong> from all{' '}
            {trail.accountStatuses?.length || 0} AWS accounts. This action cannot be undone.
          </Typography>
          {trail.s3BucketIsNew && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteBucket}
                    onChange={(e) => setDeleteBucket(e.target.checked)}
                    sx={{ color: '#C62828', '&.Mui-checked': { color: '#C62828' } }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem' }}>
                    Also delete S3 bucket <strong>{trail.s3BucketName}</strong>
                  </Typography>
                }
              />
              {deleteBucket && (
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#C62828', fontSize: '0.8rem', ml: 4 }}>
                  All log data in this bucket will be permanently deleted.
                </Typography>
              )}
            </Box>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteDialogOpen(false); setDeleteBucket(false); }}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            sx={{
              backgroundColor: '#C62828',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              '&:hover': { backgroundColor: '#B71C1C' },
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Accounts Dialog */}
      <Dialog
        open={addAccountsDialogOpen}
        onClose={() => { setAddAccountsDialogOpen(false); setSelectedNewAccounts([]); setAccountSearchQuery(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
          Add Accounts
        </DialogTitle>
        <DialogContent>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', mb: 2 }}>
            Select accounts to add to trail <strong>{trail.name}</strong>.
          </Typography>
          {realmAccounts.length === 0 ? (
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999' }}>
              No additional accounts available to add.
            </Typography>
          ) : (
            <>
            <TextField
              placeholder="Search accounts..."
              value={accountSearchQuery}
              onChange={(e) => setAccountSearchQuery(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              {realmAccounts.filter((account) => {
                if (!accountSearchQuery.trim()) return true;
                const q = accountSearchQuery.toLowerCase().trim();
                return account.name.toLowerCase().includes(q) || account.accountId.includes(q);
              }).map((account) => (
                <Box
                  key={account.accountId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    borderBottom: '1px solid #F0F0F0',
                    '&:hover': { backgroundColor: '#F9FBFD' },
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setSelectedNewAccounts((prev) =>
                      prev.includes(account.accountId)
                        ? prev.filter((id) => id !== account.accountId)
                        : [...prev, account.accountId]
                    )
                  }
                >
                  <Checkbox
                    checked={selectedNewAccounts.includes(account.accountId)}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.875rem' }}>
                      {account.name}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999', fontSize: '0.75rem' }}>
                      {account.accountId}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            </>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setAddAccountsDialogOpen(false); setSelectedNewAccounts([]); setAccountSearchQuery(''); }}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAccountsConfirm}
            disabled={addingAccounts || selectedNewAccounts.length === 0}
            variant="contained"
            sx={{
              backgroundColor: '#4698D3',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              '&:hover': { backgroundColor: '#3A7BB0' },
            }}
          >
            {addingAccounts ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              `Add Selected (${selectedNewAccounts.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Account Confirmation Dialog */}
      <Dialog
        open={removeAccountDialogOpen}
        onClose={() => { setRemoveAccountDialogOpen(false); setAccountToRemove(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
          Remove Account
        </DialogTitle>
        <DialogContent>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666', mb: 1 }}>
            Are you sure you want to remove this account from trail <strong>{trail.name}</strong>?
          </Typography>
          {accountToRemove && (
            <Paper elevation={0} sx={{ p: 2, mt: 1, border: '1px solid #E5E5E5', borderRadius: '6px', backgroundColor: '#F9FBFD' }}>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                {accountToRemove.accountName}
              </Typography>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999', fontSize: '0.8rem' }}>
                {accountToRemove.accountId}
              </Typography>
            </Paper>
          )}
          <Typography variant="body2" sx={{ color: '#C62828', mt: 2, fontFamily: 'Metropolis, sans-serif' }}>
            The trail will be deleted from this account. This action cannot be undone.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setRemoveAccountDialogOpen(false); setAccountToRemove(null); }}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveAccountConfirm}
            disabled={removingAccount}
            variant="contained"
            sx={{
              backgroundColor: '#C62828',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              '&:hover': { backgroundColor: '#B71C1C' },
            }}
          >
            {removingAccount ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default TrailDetail;
